import { action, makeObservable, observable } from "mobx";
import {
  createFile,
  getFile,
  softDeleteFile,
  hardDeleteFile,
  updateFile,
  listFiles,
  getUploadUrl,
  getContactFiles,
  generateAvatar,
  validateContactSchema,
  attachDocument,
  createDocument
} from "../../services";

class FileStore {
  // 1. POST /v1/file/create response
  fileCreated = {
    file: {
      id: '',
      name: '',
      folder_id: '',
      tenant_id: '',
      created_by: '',
      status: '',
      file_type: '',
      size: 0,
      created_at: '',
      updated_at: '',
      deleted_at: null,
      metadata: { size_mb: 0 }
    },
    upload_url: ''
  };

  // 2. GET /v1/file/{id} response
  fileRetrieved = {
    id: '',
    name: '',
    folder_id: '',
    tenant_id: '',
    created_by: '',
    status: '',
    file_type: '',
    size: 0,
    created_at: '',
    updated_at: '',
    deleted_at: null,
    metadata: { version: '' }
  };

  // 3. POST /v1/file/{id} soft delete response
  fileSoftDeleted = {
    message: '',
    file: {
      id: '',
      name: '',
      folder_id: '',
      tenant_id: '',
      created_by: '',
      status: '',
      file_type: '',
      size: 0,
      created_at: '',
      updated_at: '',
      deleted_at: '',
      metadata: null
    },
    request_id: ''
  };

  // 4. POST /v1/file/purge/{id} hard delete response
  fileHardDeleted = {
    message: '',
    deleted: false,
    request_id: ''
  };

  // 5. POST /v1/file/update response
  fileUpdated = {
    id: '',
    name: '',
    folder_id: '',
    tenant_id: '',
    created_by: '',
    status: '',
    file_type: '',
    size: 0,
    created_at: '',
    updated_at: '',
    deleted_at: null,
    metadata: { version: '', category: '' }
  };

  // 6. POST /v1/file/list response
  fileList = {
    items: [
      {
        id: '',
        name: '',
        folder_id: '',
        tenant_id: '',
        created_by: '',
        status: '',
        file_type: '',
        size: 0,
        created_at: '',
        updated_at: '',
        deleted_at: null,
        metadata: null
      }
    ],
    total: 0,
    page: 1,
    page_size: 20,
    has_next: false
  };

  // 7. POST /v1/file/upload-url response
  uploadUrl = {
    upload_url: '',
    s3_path: ''
  };

  // 8. POST /v1/file/contact-files response
  contactFiles = {
    files: [
      {
        name: '',
        url: '',
        s3url: '',
        size: 0,
        last_modified: '',
        key: ''
      }
    ],
    total: 0,
    page: 1,
    page_size: 20,
    has_next: false
  };

  // 9. POST /v1/file/generate-avatar response
  avatarGenerated = {
    success: false,
    file_name: '',
    file_url: '',
    tenant_id: '',
    instructions: '',
    size: '',
    quality: '',
    message: ''
  };

  // 10. POST /v1/file/validate-contact-schema response
  contactSchemaValidated = {
    status_code: 0,
    message: '',
    is_valid: false,
    errors: [],
    invalid_rows: [],
    total_rows: 0,
    valid_rows: 0
  };

  constructor() {
    makeObservable(this, {
      // Observables
      fileCreated: observable,
      fileRetrieved: observable,
      fileSoftDeleted: observable,
      fileHardDeleted: observable,
      fileUpdated: observable,
      fileList: observable,
      uploadUrl: observable,
      contactFiles: observable,
      avatarGenerated: observable,
      contactSchemaValidated: observable,
      
      setFileCreated: action,
      setFileRetrieved: action,
      setFileSoftDeleted: action,
      setFileHardDeleted: action,
      setFileUpdated: action,
      setFileList: action,
      setUploadUrl: action,
      setContactFiles: action,
      setAvatarGenerated: action,
      setContactSchemaValidated: action,
      
      createFile: action,
      getFile: action,
      softDeleteFile: action,
      hardDeleteFile: action,
      updateFile: action,
      listFiles: action,
      getUploadUrl: action,
      getContactFiles: action,
      generateAvatar: action,
      validateContactSchema: action,
      attachDocument: action,
      createDocument: action
    });
  }

  setFileCreated = (data) => {
    this.fileCreated = {
      file: {
        id: '',
        name: '',
        folder_id: '',
        tenant_id: '',
        created_by: '',
        status: '',
        file_type: '',
        size: 0,
        created_at: '',
        updated_at: '',
        deleted_at: null,
        metadata: { size_mb: 0 },
        ...data?.file
      },
      upload_url: '',
      ...data
    };
  };

  setFileRetrieved = (data) => {
    this.fileRetrieved = {
      id: '',
      name: '',
      folder_id: '',
      tenant_id: '',
      created_by: '',
      status: '',
      file_type: '',
      size: 0,
      created_at: '',
      updated_at: '',
      deleted_at: null,
      metadata: { version: '' },
      ...data
    };
  };

  setFileSoftDeleted = (data) => {
    this.fileSoftDeleted = {
      message: '',
      file: {
        id: '',
        name: '',
        folder_id: '',
        tenant_id: '',
        created_by: '',
        status: '',
        file_type: '',
        size: 0,
        created_at: '',
        updated_at: '',
        deleted_at: '',
        metadata: null,
        ...data?.file
      },
      request_id: '',
      ...data
    };
  };

  setFileHardDeleted = (data) => {
    this.fileHardDeleted = {
      message: '',
      deleted: false,
      request_id: '',
      ...data
    };
  };

  setFileUpdated = (data) => {
    this.fileUpdated = {
      id: '',
      name: '',
      folder_id: '',
      tenant_id: '',
      created_by: '',
      status: '',
      file_type: '',
      size: 0,
      created_at: '',
      updated_at: '',
      deleted_at: null,
      metadata: { version: '', category: '' },
      ...data
    };
  };

  setFileList = (data) => {
    this.fileList = {
      items: [
        {
          id: '',
          name: '',
          folder_id: '',
          tenant_id: '',
          created_by: '',
          status: '',
          file_type: '',
          size: 0,
          created_at: '',
          updated_at: '',
          deleted_at: null,
          metadata: null
        }
      ],
      total: 0,
      page: 1,
      page_size: 20,
      has_next: false,
      ...data
    };
  };

  setUploadUrl = (data) => {
    this.uploadUrl = {
      upload_url: '',
      s3_path: '',
      ...data
    };
  };

  setContactFiles = (data) => {
    this.contactFiles = {
      files: [
        {
          name: '',
          url: '',
          s3url: '',
          size: 0,
          last_modified: '',
          key: ''
        }
      ],
      total: 0,
      page: 1,
      page_size: 20,
      has_next: false,
      ...data
    };
  };

  setAvatarGenerated = (data) => {
    this.avatarGenerated = {
      success: false,
      file_name: '',
      file_url: '',
      tenant_id: '',
      instructions: '',
      size: '',
      quality: '',
      message: '',
      ...data
    };
  };

  setContactSchemaValidated = (data) => {
    this.contactSchemaValidated = {
      status_code: 0,
      message: '',
      is_valid: false,
      errors: [],
      invalid_rows: [],
      total_rows: 0,
      valid_rows: 0,
      ...data
    };
  };

  createFile = async (body) => {
    const { data } = await createFile(body);
    this.setFileCreated(data);
    return data;
  };

  getFile = async (id, body) => {
    const { data } = await getFile(id, body);
    this.setFileRetrieved(data);
    return data;
  };

  softDeleteFile = async (id, body) => {
    const { data } = await softDeleteFile(id, body);
    this.setFileSoftDeleted(data);
    return data;
  };

  hardDeleteFile = async (id, body) => {
    const { data } = await hardDeleteFile(id, body);
    this.setFileHardDeleted(data);
    return data;
  };

  updateFile = async (body) => {
    const { data } = await updateFile(body);
    this.setFileUpdated(data);
    return data;
  };

  listFiles = async (body) => {
    const { data } = await listFiles(body);
    this.setFileList(data);
    return data;
  };

  getUploadUrl = async (body) => {
    const { data } = await getUploadUrl(body);
    this.setUploadUrl(data);
    return data;
  };

  getContactFiles = async (body) => {
    const { data } = await getContactFiles(body);
    this.setContactFiles(data);
    return data;
  };

  generateAvatar = async (body) => {
    const { data } = await generateAvatar(body);
    this.setAvatarGenerated(data);
    return data;
  };

  validateContactSchema = async (body) => {
    const { data } = await validateContactSchema(body);
    this.setContactSchemaValidated(data);
    return data;
  };

  attachDocument = async (body) => {
    const { data } = await attachDocument(body);
    return data;
  };

  createDocument = async (body) => {
    const { data } = await createDocument(body);
    return data;
  };
}

export default FileStore;
